import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { useTenantRoles, type ProvisionUserInput } from '../api';
import { ProvisionUserForm } from './ProvisionUserForm';

/**
 * Dialog wrapper around {@link ProvisionUserForm} (PTV2-1426). Sources the tenant's role catalogue
 * ({@code useTenantRoles}, only while open) so the form can offer roles as a dropdown instead of free
 * text — consistent with {@code AssignTenantUserRolesDialog}.
 */
export function ProvisionTenantUserDialog({
  open,
  tenantUuid,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  readonly tenantUuid: string;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: Omit<ProvisionUserInput, 'tenantUuid'>) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const rolesQuery = useTenantRoles(open ? tenantUuid : '');
  const roleOptions = useMemo(() => (rolesQuery.data ?? []).map((r) => r.name), [rolesQuery.data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.detail.users.provisionTitle')}</DialogTitle>
        </DialogHeader>
        <ProvisionUserForm
          roleOptions={roleOptions}
          rolesLoading={rolesQuery.isPending}
          onSubmit={onSubmit}
          pending={pending}
          submitError={submitError}
        />
      </DialogContent>
    </Dialog>
  );
}
